<template>
  <div>
    <!-- eslint-disable vue/no-v-html -->
    <div v-html="displayableContent" />
    <!-- eslint-enable -->

    <v-card-actions
      v-if="canBeExpanded"
      class="read-more-container"
      :class="{'expanded': !showCollapsed}"
    >
      <v-spacer />

      <v-btn
        small
        icon
        outlined
        v-bind="{ dark }"
        @click="toggleCollapsed"
      >
        <v-icon
          v-if="showCollapsed"
          title="Show full comment"
        >
          expand_more
        </v-icon>
        <v-icon
          v-else
          title="Minimize comment"
        >
          expand_less
        </v-icon>
      </v-btn>
    </v-card-actions>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import DOMPurify from 'dompurify'
import { marked } from 'marked'
import truncateHtml from 'truncate-html'

import { alertHandler } from '@/alertHandler'
import { nl2br } from '@/utilities/string-utilities'

import { LEGAL_CASE_NAMESPACE } from '@/store/legal-case'

const EXCERPT_MAX_NUM_CHARS = 400
const EXCERPT_MARGIN_NUM_CHARS = 20

export default {
  name: 'DocketEntryCommentViewer',

  model: {
    prop: 'comment',
  },

  props: {
    collapse: {
      type: Boolean,
      default: false,
    },

    comment: {
      type: String,
      default: '',
    },

    dark: {
      type: Boolean,
      default: undefined,
    },

    legalCaseId: {
      type: Number,
      default: undefined,
    },

    richTextMode: {
      type: Boolean,
      default: true,
    },

    showExpandButton: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      collapseValue: false,
      renderedContent: {
        collapsed: '',
        full: '',
      },
    }
  },

  computed: {
    canBeExpanded () {
      return this.showExpandButton && this.isCollapsable
    },

    displayableContent () {
      return this.isCollapsable && this.showCollapsed
        ? this.renderedContent.collapsed
        : this.renderedContent.full
    },

    isCollapsable () {
      return (this.renderedContent.full.length - this.renderedContent.collapsed.length) > EXCERPT_MARGIN_NUM_CHARS &&
        this.renderedContent.full.length > EXCERPT_MAX_NUM_CHARS
    },

    showCollapsed: {
      get () {
        return this.collapseValue
      },

      set (value) {
        this.collapseValue = value === true
        this.$emit('update:collapse', this.collapseValue)
      },
    },

    ...mapGetters(LEGAL_CASE_NAMESPACE, [
      'docketEntriesInfo',
      'docketEntriesInfoLegalCaseId',
    ]),
  },

  watch: {
    collapse: function (newValue) {
      if (newValue === this.showCollapsed === true) return
      this.showCollapsed = newValue === true
    },

    comment: async function (newValue) {
      this.renderContent(newValue)
    },
  },

  async created () {
    if (this.richTextMode && this.docketEntriesInfoLegalCaseId !== this.legalCaseId) {
      await this.loadDocketEntriesInfo({ legalCaseId: this.legalCaseId })
    }

    marked.setOptions({ breaks: true })

    /**
     * This is an extension for Marked.js that detects references to other docket
     * entries (text started by the character # followed by numbers) and renders
     * them as links.
     */
    const docketReferenceExtension = {
      level: 'inline',
      name: 'docketReference',
      renderer: token => token.html,
      start: src => src.match(/#/)?.index,
      tokenizer (src) {
        const rule = /^(#([1-9][0-9]*))([\s.:,;]|$)/
        const match = rule.exec(src)
        if (match) {
          return {
            docketId: match[2],
            html: '',
            raw: match[1],
            type: 'docketReference',
          }
        }
      },
    }

    const docketReferenceRenderer = async token => {
      if (token.type !== 'docketReference') return
      if (token.html) return

      token.html = token.raw

      if (!this.legalCaseId) return
      if (!this.docketEntriesInfo[token.docketId]) return

      if (this.docketEntriesInfo[token.docketId].deleted) token.html = `<del>${token.raw}</del>`
      else token.html = `<a href="${this.docketEntriesInfo[token.docketId].url}">${token.raw}</a>`
    }

    marked.use({
      async: true,
      extensions: [docketReferenceExtension],
      walkTokens: async token => {
        await docketReferenceRenderer(token)
      },
    })

    this.collapseValue = this.collapse === true
    this.renderContent(this.comment || '')
  },

  methods: {
    async renderContent (content) {
      if (this.richTextMode) {
        const html = await marked.parse(content)
        this.renderedContent.full = DOMPurify.
          sanitize(html).
          replaceAll('<p>&nbsp;</p>', '<p></p>').
          trim()
      } else this.renderedContent.full = nl2br(content)

      this.renderedContent.collapsed = truncateHtml(this.renderedContent.full, EXCERPT_MAX_NUM_CHARS).trim()
    },

    toggleCollapsed () {
      this.showCollapsed = this.showCollapsed === false
    },

    ...mapActions(LEGAL_CASE_NAMESPACE, [
      'loadDocketEntriesInfo',
    ]),
  },
}
</script>

<style>
.docket-reference-link {
  text-decoration: underline;
}
</style>
